import { type FC, useState, useCallback } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { type StudyApiId } from '@/model/Study.model';
import { type ReservedSampleApiId } from './SampleNames.model';
import { type SampleNameTemplateTokenV1 } from '@/model/SampleNameTemplate.model';
import { errorToast, successToast } from '@/helpers';
import Button from '@/components/UI/Button/Button';
import { ModalContainer, ModalHeader, modalAction } from '@/utils/modal';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import Switch from '@/components/UI/FormElements/Switch/Switch';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import Loading from '@/components/Loading/Loading';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Tooltip from '@/components/UI/Tooltip/Tooltip';
import styles from '@/components/Glossary/Sections/SampleNaming/Builder/SampleNameTemplateBuilder.module.scss';
import type { components } from '@/generated/internal-api/openapi-types';

type SampleApiId = components['schemas']['SampleApiId'];

const PLACEHOLDER_VALUES: Record<string, string> = {
  study_code: 'STD001',
  sample_type: 'BLD',
  animal_id: '1_1',
  group_number: '1',
  timepoint: '1H',
};

const tokenChipLabel = (token: SampleNameTemplateTokenV1): string => {
  if (_notNil(token.custom_value) && typeof token.custom_value === 'string') {
    return `"${token.custom_value}"`;
  }
  if (token.definition.type === 'separator') {
    return token.definition.value;
  }
  if (token.definition.value === 'sample_meta' && _notNil(token.metadata_glossary_title)) {
    return token.metadata_glossary_title as string;
  }
  return token.definition.label;
};

const tokenPreviewValue = (token: SampleNameTemplateTokenV1): string => {
  if (typeof token.custom_value === 'string') {
    return token.custom_value;
  }
  if (token.definition.type === 'separator') {
    return token.definition.value;
  }
  if (token.definition.value === 'sample_meta') {
    return (token.metadata_glossary_title as string | null | undefined) ?? 'Meta';
  }
  return PLACEHOLDER_VALUES[token.definition.value] ?? token.definition.label;
};

/** Determines the description text shown in the modal. */
export type RegenerateVariant = 'reserved' | 'executed' | 'resync-stale';

interface RegenerateNamesModalProps {
  studyApiId: StudyApiId;
  /** Reserved / planned stale IDs (collectable_samples) — always sent to the server. Ignored when sampleApiIds is provided. */
  reservedSampleApiIds?: Array<ReservedSampleApiId>;
  /** Collected-status stale IDs — sent only when includeCollected is true. When provided, enables the count breakdown display for the stale variant. */
  collectedSampleApiIds?: Array<ReservedSampleApiId>;
  /** Sample IDs (spl_*). When provided, uses the sample-based regenerate endpoint instead of the reserved-sample endpoint. */
  sampleApiIds?: Array<SampleApiId>;
  /** Approximate reserved/planned sample count for the resync-stale/reserved variant breakdown display. */
  reservedCount?: number;
  /** Approximate collected sample count for the resync-stale/reserved variant breakdown display. */
  collectedCount?: number;
  /** Whether any reserved/planned stale samples exist — gates the reserved count in the resync-stale breakdown. */
  hasStaleReservedSamples?: boolean;
  /** Whether any collected stale samples exist — gates the collected count in the resync-stale breakdown. */
  hasStaleCollectedSamples?: boolean;
  closeModal: () => void;
  onSuccess: (opts: { includeCollected: boolean }) => void;
  hideIncludeCollected?: boolean;
  variant?: RegenerateVariant;
}

interface RegenerateFormValues {
  reason_for_change: string;
}

/**
 * Confirmation modal for regenerating reserved sample names against the active template.
 * Shows the active template's token chips, a live preview using placeholder values,
 * and an option to include already-collected samples.
 */
const RegenerateNamesModal: FC<RegenerateNamesModalProps> = ({
  studyApiId,
  reservedSampleApiIds = [],
  collectedSampleApiIds,
  sampleApiIds,
  reservedCount,
  collectedCount,
  hasStaleReservedSamples,
  hasStaleCollectedSamples,
  closeModal,
  onSuccess,
  hideIncludeCollected = false,
  variant = 'reserved',
}) => {
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const [includeCollected, setIncludeCollected] = useState(
    hasStaleCollectedSamples === true && hasStaleReservedSamples !== true
  );

  const isBySampleIds = _notNil(sampleApiIds) && sampleApiIds.length > 0;
  const totalCount = isBySampleIds
    ? sampleApiIds.length
    : reservedSampleApiIds.length + (collectedSampleApiIds?.length ?? 0);

  const formMethods = useForm<RegenerateFormValues>({
    mode: 'onChange',
    defaultValues: { reason_for_change: '' },
  });

  const { response: templatesResponse, loading: templatesLoading } = useApiHook({
    endpoint: 'GET /api/v1/team/sample-name-templates',
    options: { onError: { slug: 'regenerate-names-load-template', toast: false } },
  });

  const { loading: isRegeneratingByReserved, invoke: executeRegenerateByReserved } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/regenerate',
    path: { studyApiId },
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, capture: false, slug: 'regenerate-reserved-sample-names' },
    },
  });

  const { loading: isRegeneratingBySample, invoke: executeRegenerateBySample } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/regenerate',
    path: { studyApiId },
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, capture: false, slug: 'regenerate-sample-names-by-id' },
    },
  });

  const { loading: isResyncingStale, invoke: executeResyncStale } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/resync-stale',
    path: { studyApiId },
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: true, capture: false, slug: 'resync-stale-reserved-sample-names' },
    },
  });

  const isRegenerating = isRegeneratingByReserved || isRegeneratingBySample || isResyncingStale;

  const activeTemplate =
    templatesResponse?.type === 'success'
      ? templatesResponse.body.data.find((t: { status: string }) => t.status === 'active')
      : undefined;

  const sortedTokens: Array<SampleNameTemplateTokenV1> | null = _notNil(activeTemplate)
    ? ([...activeTemplate.tokens] as Array<SampleNameTemplateTokenV1>).sort((a, b) => a.position - b.position)
    : null;

  const previewName = _notNil(sortedTokens) ? sortedTokens.map(tokenPreviewValue).join('') : null;

  const executeRegenerate = useCallback(
    async ({ reason_for_change: rawReason }: RegenerateFormValues) => {
      try {
        const reasonForChange = rawReason?.trim();
        const reasonParam = _isNotBlank(reasonForChange) ? { reason_for_change: reasonForChange } : {};

        if (variant === 'resync-stale') {
          await executeResyncStale({
            path: { studyApiId },
            body: {
              include_collected: includeCollected,
              ...reasonParam,
            },
          });
          successToast('Sample names resynced with the active template');
          onSuccess({ includeCollected });
          closeModal();
          return;
        }

        if (isBySampleIds) {
          await executeRegenerateBySample({
            path: { studyApiId },
            body: {
              sample_api_ids: sampleApiIds,
              include_collected: includeCollected,
              ...reasonParam,
            },
          });
        } else {
          const idsToRegenerate = [
            ...reservedSampleApiIds,
            ...(includeCollected && _notNil(collectedSampleApiIds) ? collectedSampleApiIds : []),
          ];
          await executeRegenerateByReserved({
            path: { studyApiId },
            body: {
              reserved_sample_api_ids: idsToRegenerate,
              include_collected: includeCollected,
              ...reasonParam,
            },
          });
        }

        successToast(`${totalCount} sample name${totalCount !== 1 ? 's' : ''} regenerated`);
        onSuccess({ includeCollected });
        closeModal();
      } catch {
        errorToast('Failed to regenerate sample names. Please try again.');
      }
    },
    [
      executeRegenerateByReserved,
      executeRegenerateBySample,
      executeResyncStale,
      studyApiId,
      sampleApiIds,
      reservedSampleApiIds,
      collectedSampleApiIds,
      includeCollected,
      isBySampleIds,
      totalCount,
      variant,
      onSuccess,
      closeModal,
    ]
  );

  const handleConfirm = useCallback(
    (values: RegenerateFormValues) => {
      if (includeCollected) {
        openModal('CONFIRM_REGENERATE_COLLECTED_SAMPLES', {
          onClick: () => executeRegenerate(values),
        });
        return;
      }
      void executeRegenerate(values);
    },
    [includeCollected, executeRegenerate, openModal]
  );

  return (
    <ModalContainer size="medium">
      <ModalHeader title="Regenerate sample names" closeModal={closeModal} />
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(handleConfirm)}>
          <div className="pa3">
            {variant === 'reserved' && (
              <p className="mv0">
                <div>
                  Regenerate sample names using the active template: <strong>{activeTemplate?.name}</strong>
                </div>
                {_notNil(reservedCount) && _notNil(collectedCount) ? (
                  <span className="db f6 mt1">
                    <strong>{totalCount}</strong> selected: <strong>{reservedCount}</strong>{' '}
                    {isBySampleIds ? 'planned' : 'reserved / planned'}, <strong>{collectedCount}</strong> collected
                  </span>
                ) : (
                  <span className="db f6 mt1">
                    <strong>{totalCount}</strong> reserved sample name{totalCount !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            )}
            {variant === 'executed' && (
              <div>
                <p className="mv0 dark-gray">
                  Regenerate <strong>{totalCount}</strong> executed sample name{totalCount !== 1 ? 's' : ''} against the
                  active template: <strong>{activeTemplate?.name}</strong>.
                </p>
                <p>Verify no printed labels are impacted by this action.</p>
              </div>
            )}
            {variant === 'resync-stale' && (
              <p className="mv0 dark-gray">
                {hasStaleReservedSamples && hasStaleCollectedSamples
                  ? 'Sample names are out of sync with the active template. This action will regenerate all stale sample names using the active template: '
                  : hasStaleReservedSamples
                    ? 'Sample names are out of sync with the active template. This action will regenerate all stale reserved / planned sample names using the active template: '
                    : 'Names for collected samples are out of sync with the active template. This action will regenerate stale collected sample names using the active template: '}
                <strong>{activeTemplate?.name}</strong>
              </p>
            )}
            <div className="mt3">
              {templatesLoading && <Loading />}
              {!templatesLoading && _notNil(sortedTokens) && (
                <div className="flex flex-column" style={{ gap: '10px' }}>
                  <div className={styles.previewContainer}>
                    <p className="db mt1">Preview</p>
                    <div className={styles.preview}>
                      {_isNotBlank(previewName) ? (
                        <p className={styles.previewName}>
                          {sortedTokens!.map((token, i) => {
                            const value = tokenPreviewValue(token);
                            if (token.definition.type === 'separator') {
                              return <span key={i}>{value}</span>;
                            }
                            return (
                              <Tooltip key={i} render={tokenChipLabel(token)}>
                                <span>{value}</span>
                              </Tooltip>
                            );
                          })}
                        </p>
                      ) : (
                        <p className={styles.previewEmpty}>No tokens configured</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {!templatesLoading && _isNil(activeTemplate) && <p className="mv0 f6 gray">No active template found</p>}
            </div>
            <ReasonForChange />
            <div className="flex items-center mt4 b--moon-gray" style={{ gap: '12px' }}>
              <Button primary submit disabled={isRegenerating} testId="regenerate-confirm-button">
                {isRegenerating ? 'Regenerating…' : 'Regenerate'}
              </Button>
              <Button plain onClick={closeModal} testId="regenerate-cancel-button">
                Cancel
              </Button>
              {!hideIncludeCollected && (
                <>
                  <Switch
                    value={includeCollected}
                    onChange={setIncludeCollected}
                    testId="regenerate-include-collected-switch"
                    ariaLabel="Include collected samples"
                  />
                  <span className="dark-gray f5 mr2">Include collected samples</span>
                </>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </ModalContainer>
  );
};

export default RegenerateNamesModal;
